import {
  ACCENT_COLUMN_INDEX,
  ARCHITRAVE,
  COLUMN_COUNT,
  COLUMN_PIECES,
  CORNICE_PIECES,
  STYLOBATE_PIECES,
  type CoursePiece,
} from '../config/colonnade';
import type { JSX } from 'react';
import { COLONNADE_COPY } from '../config/content';
import { useAssembleOnView } from '../hooks/useAssembleOnView';

import styles from './Colonnade.module.css';

/** One carved element. Its `data-part` selects that part's size, borders and gradients in CSS —
 *  never an inline style, which the page's policy refuses (REQ-LANDING-008). */
function Part({ id, className, group }: { id: string; className?: string; group?: string }) {
  return <div className={className} data-part={id} data-group={group} />;
}

/** A course that is a single part and moves on its own — the architrave, each stylobate step. */
function Course({ piece }: { piece: CoursePiece }) {
  return <Part className={styles.piece} id={piece.id} group={piece.group} />;
}

/** One column, bottom-aligned. `data-column` selects the rule that sets its `--column-index`,
 *  which drives its place in the assembly stagger. */
function Column({ index }: { index: number }) {
  const classes = [styles.piece, styles.column, index === ACCENT_COLUMN_INDEX && styles.accent]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} data-group="column" data-column={index}>
      {COLUMN_PIECES.map((part) => (
        <Part key={part.id} id={part.id} />
      ))}
    </div>
  );
}

/**
 * The decorative structure beside the copy.
 *
 * Every element comes from the structure table in `config/colonnade.ts` — six columns of six
 * parts each are generated, not transcribed. The accent column is chosen by index, so moving
 * the one inked column in the design is a one-value edit rather than a hunt through
 * thirty-six near-identical elements.
 *
 * Hidden from assistive technology in its entirety. It is decoration: it carries no information
 * that the copy beside it does not, and announcing "abacus, echinus, necking, shaft…" thirty-six
 * times would be pure noise to a screen-reader user.
 */
export function Colonnade(): JSX.Element {
  const { ref, assembled } = useAssembleOnView<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={styles.panel}
      // Omitted rather than set to "false" while at rest, so the attribute's presence is the
      // state and CSS has one thing to match on.
      data-assembled={assembled ? 'true' : undefined}
      aria-hidden="true"
    >
      <span className={styles.hint}>{COLONNADE_COPY.hint}</span>

      {/* The cornice and its dentil course arrive as one piece — they are one moulding. */}
      <div className={`${styles.piece} ${styles.corniceGroup}`} data-group="cornice">
        {CORNICE_PIECES.map((piece) => (
          <Part key={piece.id} id={piece.id} />
        ))}
      </div>

      <Course piece={ARCHITRAVE} />

      <div className={styles.row}>
        {Array.from({ length: COLUMN_COUNT }, (_, index) => (
          <Column key={index} index={index} />
        ))}
      </div>

      {STYLOBATE_PIECES.map((piece) => (
        <Course key={piece.id} piece={piece} />
      ))}
    </div>
  );
}
